import React, { Component, ErrorInfo } from "react";
import { AppFallback } from "./fallbacks/AppFallback";
import { BaseFallback } from "./fallbacks/BaseFallback";

/**
 * Internal state for the ErrorBoundary component.
 */
interface ErrorBoundaryState {
  /**
   * Indicates whether an error has been caught
   */
  hasError: boolean;
  /**
   * The error that was caught by the error boundary
   */
  error: Error | null;
}

/**
 * Props for the ErrorBoundary component.
 */
export interface ErrorBoundaryProps {
  /**
   * Child components to be wrapped by the error boundary
   */
  children?: React.ReactNode;

  /**
   * Optional callback invoked when an error is caught
   */
  onError?: (error: Error, info: ErrorInfo) => void;

  /**
   * The level of the error boundary - determines which fallback UI to render
   */
  level?: "app" | "route" | "component";
}

/**
 * A component that catches errors in children components and displays a fallback UI instead of crashing the entire app.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  displayError: boolean;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };

    this.displayError =
      import.meta.env.VITE_ENABLE_VERBOSE_ERRORS === "true" ||
      import.meta.env.MODE === "development";
  }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  /**
   * Catches exceptions generated in descendant components. Unhandled exceptions will cause the entire component tree to unmount.
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (!this.state.hasError) return;

    // Reset if children changed (e.g. route navigation swapped the child page)
    if (prevProps.children !== this.props.children) {
      this.resetError();
      return;
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.level === "app") {
        return (
          <AppFallback
            error={this.state.error}
            level="app"
            displayError={this.displayError}
          />
        );
      }

      if (this.props.level === "route") {
        return (
          <AppFallback
            error={this.state.error}
            level="route"
            displayError={this.displayError}
          />
        );
      }

      if (this.props.level === "component") {
        return (
          <BaseFallback
            error={this.state.error}
            displayErrorInfo={this.displayError}
          />
        );
      }
    }

    return this.props.children;
  }
}
